import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { usePublicClient } from 'wagmi';
import { 
  PANCAKE_ROUTER_V2, 
  PANCAKE_ROUTER_V2_ABI, 
  PANCAKE_SMART_ROUTER,
  WBNB_ADDRESS 
} from '../config/contracts';

/**
 * Universal Swap Quote Hook
 * Tries multiple routers (Smart Router + V2) to find the best quote
 */
export const useUniversalSwapQuote = (fromToken, toToken, amountIn, slippageBps = 50) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [routerUsed, setRouterUsed] = useState(null);
  const publicClient = usePublicClient();

  const buildPath = useCallback((from, to) => {
    const f = from?.isNative ? WBNB_ADDRESS : from?.address;
    const t = to?.isNative ? WBNB_ADDRESS : to?.address;
    if (f?.toLowerCase() === t?.toLowerCase()) return [f];
    
    // Direct path if one is native, otherwise route through WBNB
    return from?.isNative || to?.isNative ? [f, t] : [f, WBNB_ADDRESS, t];
  }, []);

  const fetchV2Quote = async (provider, path, inAmt) => {
    try {
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_V2_ABI, provider);
      const outs = await router.getAmountsOut(inAmt, path);
      return {
        success: true,
        amountOut: outs[outs.length - 1],
        router: 'V2'
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const fetchSmartRouterQuote = async (provider, path, inAmt) => {
    try {
      // Smart Router can find optimal paths through V2, V3, and StableSwap pools
      // For now, we use V2 as fallback since Smart Router requires complex encoding
      // In production, you'd use @pancakeswap/smart-router SDK
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_V2_ABI, provider);
      const outs = await router.getAmountsOut(inAmt, path);
      return {
        success: true,
        amountOut: outs[outs.length - 1],
        router: 'Smart Router'
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const fetchQuote = useCallback(async () => {
    if (!fromToken || !toToken || !amountIn || amountIn <= 0 || !publicClient) {
      setQuote(null);
      setRouterUsed(null);
      return;
    }

    const fKey = fromToken?.isNative ? 'native' : fromToken?.address?.toLowerCase();
    const tKey = toToken?.isNative ? 'native' : toToken?.address?.toLowerCase();
    if (fKey === tKey) {
      setQuote(null);
      setRouterUsed(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const path = buildPath(fromToken, toToken);
      if (path.length < 2) {
        setQuote(null);
        setRouterUsed(null);
        setLoading(false);
        return;
      }

      const provider = new ethers.BrowserProvider(publicClient);
      const inAmt = ethers.parseUnits(
        String(amountIn),
        fromToken?.isNative ? 18 : fromToken?.decimals
      );

      // Try Smart Router first, fallback to V2
      let result = await fetchSmartRouterQuote(provider, path, inAmt);
      
      if (!result.success) {
        result = await fetchV2Quote(provider, path, inAmt);
      }

      if (!result.success) {
        throw new Error('All routers failed to provide a quote');
      }

      const outRaw = result.amountOut;
      const outVal = Number(
        ethers.formatUnits(outRaw, toToken?.isNative ? 18 : toToken?.decimals)
      );

      const minOut = (outRaw * (10000n - BigInt(slippageBps))) / 10000n;

      setQuote({
        path,
        amountOut: outVal,
        amountOutRaw: outRaw,
        minOut,
        priceImpact: 0,
        route: path.map(a => a.toLowerCase()).join(' → ')
      });
      // Remove fallback text from router name
      const cleanRouterName = result.router.replace(/\(.*fallback.*\)/gi, '').trim();
      setRouterUsed(cleanRouterName);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching universal quote:', err);
      setError(err.message);
      setLoading(false);
      setQuote(null);
      setRouterUsed(null);
    }
  }, [fromToken, toToken, amountIn, slippageBps, publicClient, buildPath]);

  useEffect(() => {
    const timer = setTimeout(fetchQuote, 300);
    return () => clearTimeout(timer);
  }, [fetchQuote]);

  return { quote, loading, error, routerUsed, refetch: fetchQuote };
};
